import { discountPercent, effectivePrice, type Product } from '@/lib/catalogue';
import { formatMoney } from '@/lib/utils';

/**
 * Carte produit — pensée pour un doigt, debout, sur un iPad.
 * Toute la carte est cliquable (cible d'au moins 96 px de haut) plutôt qu'un
 * petit bouton « ajouter » qu'on rate une fois sur trois.
 */
export default function ProductCard({
  product,
  onSelect,
}: {
  product: Product;
  onSelect?: (p: Product) => void;
}) {
  const remise = discountPercent(product);

  return (
    <button
      type="button"
      onClick={() => onSelect?.(product)}
      className="bg-card hover:border-primary/50 active:bg-accent flex min-h-24 w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors"
    >
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className="bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-mono text-xs font-medium">
            {product.lineCode}
          </span>
          <span className="font-mono text-sm font-semibold">{product.partNumber}</span>
          {product.isFeatured && (
            <span className="rounded bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
              Vedette
            </span>
          )}
        </div>

        <p className="text-base leading-snug font-medium">{product.description}</p>

        <p className="text-muted-foreground mt-1 text-sm">
          {product.uom}
          {product.packQty > 1 && ` · paquet de ${product.packQty}`}
        </p>
      </div>

      <div className="shrink-0 text-right">
        <div className="text-2xl font-bold tabular-nums">{formatMoney(effectivePrice(product))}</div>
        {remise !== null && (
          <div className="mt-0.5 flex items-center justify-end gap-2">
            {/* Prix liste barré : le rep doit pouvoir montrer l'économie au client. */}
            <span className="text-muted-foreground text-sm line-through tabular-nums">
              {formatMoney(product.listPrice)}
            </span>
            <span className="rounded bg-green-600/15 px-1.5 py-0.5 text-xs font-semibold text-green-700 dark:text-green-400">
              −{remise} %
            </span>
          </div>
        )}
      </div>
    </button>
  );
}
